import { API_V1_PREFIX, type ApiError, type AuthResponse } from '@orbit-sync/shared-types';
import { clearAdminToken, getAdminToken } from '../adminTokenStorage.js';
import { clearAccessToken, getAccessToken, setAccessToken } from '../tokenStorage.js';
import { ApiRequestError, NetworkError } from './errors.js';

/**
 * Empty in development and in the Compose stack: the Vite dev proxy and nginx
 * both serve the API from the same origin as the page. Set VITE_API_BASE_URL
 * only when the API lives somewhere else.
 */
export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Set false for endpoints that must not carry credentials (register, login). */
  authenticated?: boolean;
  /**
   * Which credential to send. `admin` attaches the administrator session token
   * instead of the user's - the two are never interchangeable, and a 401
   * clears only the one that was sent.
   */
  as?: 'user' | 'admin';
  query?: Record<string, string | number | undefined>;
  /** Survive the page unloading - for sign-out as the tab closes. */
  keepalive?: boolean;
}

export function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE_URL}${API_V1_PREFIX}${path}`;

  if (!query) {
    return url;
  }

  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) {
      params.set(key, String(value));
    }
  }

  const serialised = params.toString();
  return serialised ? `${url}?${serialised}` : url;
}

// ---- Session refresh --------------------------------------------------------

export type RefreshOutcome =
  /** A fresh access token is stored. */
  | { status: 'ok'; session: AuthResponse }
  /** The server says the session is over; the token has been cleared with its reason. */
  | { status: 'ended' }
  /** Could not ask (offline, server restarting). The current token is left alone. */
  | { status: 'failed' };

let refreshing: Promise<RefreshOutcome> | null = null;
const refreshListeners = new Set<(session: AuthResponse) => void>();

/** Called with every successful refresh, e.g. to pick up a renamed account. */
export function onSessionRefreshed(listener: (session: AuthResponse) => void): () => void {
  refreshListeners.add(listener);
  return () => refreshListeners.delete(listener);
}

/**
 * Swaps the session cookie for a fresh access token.
 *
 * Single-flight: every caller that needs a token at the same moment - several
 * requests failing together, the socket reconnecting, the keeper's timer -
 * shares one request. The session cookie is the credential, so this works
 * however long ago the access token expired - after a night asleep, say - as
 * long as the SESSION is still alive. Only an explicit "this session is over"
 * from the server signs the user out; a network failure never does.
 */
export function refreshSession(): Promise<RefreshOutcome> {
  refreshing ??= performRefresh().finally(() => {
    refreshing = null;
  });

  return refreshing;
}

async function performRefresh(): Promise<RefreshOutcome> {
  const token = getAccessToken();
  let response: Response;

  try {
    response = await fetch(buildUrl('/auth/refresh'), {
      method: 'POST',
      credentials: 'include',
      // Only consulted by the server when there is no session cookie: a token
      // from before server-side sessions is turned into one, once.
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
  } catch {
    return { status: 'failed' };
  }

  if (response.ok) {
    const session = (await response.json()) as AuthResponse;
    setAccessToken(session.accessToken.token);

    for (const listener of refreshListeners) {
      listener(session);
    }

    return { status: 'ok', session };
  }

  if (response.status === 401) {
    const body = await readApiError(response);
    clearAccessToken(token ? body.error.message : null);
    return { status: 'ended' };
  }

  return { status: 'failed' };
}

/** Paths whose 401 means "wrong credentials", not "token expired". */
const NO_RETRY = new Set(['/auth/login', '/auth/register', '/auth/refresh', '/auth/logout']);

/**
 * The single fetch path for every API call.
 *
 * ARCHITECTURAL RULE: response and request types come from
 * @orbit-sync/shared-types. This module never declares its own DTO shapes - if a
 * type is missing, it is added there and imported by both apps.
 *
 * A user request refused with 401 refreshes the session once and is retried,
 * so an access token that ran out between two requests is invisible to the
 * person using the app.
 */
export async function request<TResponse>(
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  const response = await send(path, options);

  if (response.ok) {
    // 204 and friends have no body to parse.
    return (response.status === 204 ? undefined : await response.json()) as TResponse;
  }

  throw new ApiRequestError(response.status, await readApiError(response));
}

async function send(path: string, options: RequestOptions, retried = false): Promise<Response> {
  const { method = 'GET', body, signal, authenticated = true, query, as = 'user' } = options;

  const headers: Record<string, string> = {};
  const token = !authenticated ? null : as === 'admin' ? getAdminToken() : getAccessToken();

  if (body !== undefined) {
    headers['content-type'] = 'application/json';
  }

  if (token) {
    headers.authorization = `Bearer ${token}`;
  }

  let response: Response;

  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      credentials: 'include',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
      ...(options.keepalive ? { keepalive: true } : {}),
    });
  } catch (error) {
    // Re-throw aborts untouched: a cancelled request is not a failure to report.
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }

    throw new NetworkError(error);
  }

  if (response.status !== 401 || !token) {
    return response;
  }

  if (as === 'admin') {
    // Administrator sessions are opaque and do not refresh: this one is over.
    clearAdminToken();
    return response;
  }

  if (retried || NO_RETRY.has(path)) {
    return response;
  }

  // The access token ran out, or the session ended. Refreshing tells which:
  // a fresh token means try again; an ended session has already signed out.
  const outcome = await refreshSession();
  return outcome.status === 'ok' ? send(path, options, true) : response;
}

/** A 4xx/5xx should carry an `ApiError` body, but a proxy error page might not. */
export async function readApiError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as Partial<ApiError>;

    if (body.error?.code && body.error.message) {
      return body as ApiError;
    }
  } catch {
    // Fall through to the generic shape below.
  }

  return {
    error: {
      code: response.status >= 500 ? 'internal_error' : 'validation_failed',
      message: `Request failed with status ${response.status}`,
    },
  };
}
